import { useMutation } from '@apollo/client'
import { Alert, AlertType, theme } from '@focaldata/cin-ui-components'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import ConfirmationCostBreakdown from '../../../../controls/ConfirmationCostBreakdown'
import ConfirmationCostBreakdownLoader from '../../../../controls/ConfirmationCostBreakdown/loading'
import ConfirmationProjectOverview from '../../../../controls/ConfirmationProjectOverview'
import ConfirmationProjectOverviewLoader from '../../../../controls/ConfirmationProjectOverview/loading'
import ConfirmationReviewDataQuality from '../../../../controls/ConfirmationReviewDataQuality'
import ConfirmationStartDate from '../../../../controls/ConfirmationStartDate'
import ConfirmationStartDateLoader from '../../../../controls/ConfirmationStartDate/loading'
import SurveyLaunchedPage from '../../../../controls/SurveyLaunchedPage'
import { CreateSurveyOrderV2MutationVariables } from '../../../../data/gql-gen/dashboard/graphql'
import {
  CREATE_SURVEY_ORDER_V2,
  CreateSurveyOrderData
} from '../../../../data/gql/order/mutations/createSurveyOrder'
import { LoggerErrorType } from '../../../../data/logger'
import { Locale } from '../../../../data/model/fieldwork'
import { useUpdateFieldworkV2 } from '../../../../hooks/audience/createOrUpdateFieldworkV2'
import useAudienceQuestionsCounter from '../../../../hooks/audience/useAudienceQuestionsCounter'
import useGetDraftAudience from '../../../../hooks/audience/useGetDraftAudience'
import useGetDraftQuestionnaire from '../../../../hooks/questionnaire/useGetDraftQuestionnaire'
import useQuestionnaireValidation from '../../../../hooks/questionnaire/useQuestionnaireValidation'
import useCalculateLengthOfInterview from '../../../../hooks/useCalculateLengthOfInterview'
import { useFieldwork } from '../../../../hooks/useFieldwork'
import { useGetFeasibilityEstimateData } from '../../../../hooks/useGetFeasibilityEstimateData'
import usePollCostQuote from '../../../../hooks/usePollCostQuote'
import { useProjectId } from '../../../../hooks/useProjectId'
import { useSurveyId } from '../../../../hooks/useSurveyId'
import ConfirmationLayout from '../../../../layouts/ConfirmationLayout'
import useTranslationsCompletenessValidation from '../../../../modules/Translations/hooks/useTranslationsCompletenessValidation'

import {
  calculateTotalCost,
  captureApolloError,
  getResultsPath,
  handleShowPreview
} from '../../../../utils/HelperFunctions'
import {
  PanelSupplier,
  convertAudienceType,
  panelOptions
} from '../../../../utils/audienceHelper'
import { getLaunchErrorDetails } from '../../../../utils/launchErrorDetails'
import { getSurveyLiveConfig } from '../../../../utils/surveyLiveConfig'
import BottomNav from '../BottomNav'
import {
  getMaxDateBetweenDefaultAndEndDate,
  getMaxEstimatedDeliveryDays,
  getStartAndEndDatesAdjusted,
  getStartAndEndDatesFormatted
} from './Confirmation.utils'
import { calculateTotalQuestions } from './calculateTotalQuestions'

const Confirmation: React.FC = () => {
  const projectId = useProjectId()
  const surveyId = useSurveyId()
  const navigate = useNavigate()
  const [isPurchaseGoLiveClicked, setIsPurchaseGoLiveClicked] =
    useState<boolean>(false)
  const [isOrderPlaced, setIsOrderPlaced] = useState(false)
  const [isPlacingOrder, setIsPlacingOrder] = useState(false)
  const [hasCostChanged, setHasCostChanged] = useState(false)
  const [failedToLaunch, setFailedToLaunch] = useState(false)
  const [launchErrorDetails, setLaunchErrorDetails] = useState<
    string | undefined
  >()

  const { loadingCountAudienceQuestions } = useAudienceQuestionsCounter()
  const { hasValidationErrors } = useQuestionnaireValidation()

  const {
    isFeasible,
    isAudienceValid,
    estimatedDeliveryTime: estimatedDeliveryDays,
    feasibility,
    loadingFeasibilityEstimate,
    loadingFieldworkEstimate
  } = useGetFeasibilityEstimateData()

  const { draftQuestionnaire, questionnaireLoading } =
    useGetDraftQuestionnaire()

  const { draftAudience, loading: loadingAudience } = useGetDraftAudience()

  // Use the extracted function for the total count
  const countTotalQuestions = calculateTotalQuestions(
    draftQuestionnaire,
    draftAudience
  )
  const isQuestionnairePresent = countTotalQuestions > 0

  // Block launch when any allocated language is below 100% translated — the
  // respondent app reads `fieldwork.languageAllocations` to offer language
  // selection, so a half-translated allocation would silently render in
  // English regardless of what the respondent picks. Hook short-circuits to
  // `false` unless "Needs translation" is set for this survey — ticked by the
  // researcher, or inferred from translations already stored against it — so
  // surveys without the Translations tab are unaffected. Called here (top of
  // the component) rather than next to its consumer below the early returns,
  // because Hooks can't be called conditionally.
  const { hasIncompleteTranslations } = useTranslationsCompletenessValidation()

  const { fieldwork, loading: loadingFieldwork } = useFieldwork()

  const panelSupplierCode = fieldwork?.audience[0].__typename
    ? convertAudienceType(fieldwork.audience[0].__typename)
    : null

  const showCostBreakdown =
    panelSupplierCode === PanelSupplier.CINT ||
    panelSupplierCode === PanelSupplier.PURESPECTRUM
  const panelSupplierName =
    panelOptions.find((p) => p.id === panelSupplierCode)?.name || ''

  const calculatedEstimatedLOI = useCalculateLengthOfInterview()

  let estimatedLengthOfInterview = fieldwork?.lengthOfInterview.unadjusted || 5

  const isEstimatedAndCalculatedSame =
    estimatedLengthOfInterview === calculatedEstimatedLOI
  if (calculatedEstimatedLOI && !isEstimatedAndCalculatedSame) {
    estimatedLengthOfInterview = calculatedEstimatedLOI
  }

  const wantedCompletes: number | undefined =
    fieldwork?.samplesNeeded.unadjusted
  const locale: Locale | undefined = fieldwork?.locale
  const fieldworkDataStartDate: string | undefined =
    fieldwork?.duration.startDate

  const [selectedDate, setSelectedDate] = useState<Date>(
    fieldworkDataStartDate ? new Date(fieldworkDataStartDate) : new Date()
  )

  const { updateFieldworkV2 } = useUpdateFieldworkV2()

  const { getCostQuoteData, loadingCostDetails, refetchGetCostQuote } =
    usePollCostQuote(estimatedLengthOfInterview)
  const { costPerIncidence, baseAmount, roundedCostPerIncidence } =
    getCostQuoteData

  const totalCost =
    roundedCostPerIncidence &&
    wantedCompletes &&
    calculateTotalCost(roundedCostPerIncidence, wantedCompletes)

  const { formattedStartDate, formattedEndDate } = getStartAndEndDatesFormatted(
    selectedDate,
    estimatedDeliveryDays
  )

  const maxEndDate = getMaxDateBetweenDefaultAndEndDate(
    fieldwork?.samplesNeededIncreaseFrequency !== null,
    formattedEndDate,
    fieldwork?.duration.endDate
  )

  const maxEstimatedDeliveryDays = getMaxEstimatedDeliveryDays(
    fieldwork?.samplesNeededIncreaseFrequency !== null,
    estimatedDeliveryDays,
    fieldwork?.duration.endDate
  )

  useEffect(() => {
    if (!fieldworkDataStartDate) return

    // It checks the startDate saved in the DB and compares it to todayDate
    // If the startDate is in the past, it updates UI to always be, at least,
    // today's date and never in the past
    setSelectedDate(
      new Date(
        getStartAndEndDatesAdjusted(
          fieldworkDataStartDate,
          estimatedDeliveryDays
        ).adjustedStartDate
      )
    )
  }, [fieldworkDataStartDate, estimatedDeliveryDays])

  const [createSurveyOrderV2, { loading: loadingCreateSurveyOrderV2 }] =
    useMutation<CreateSurveyOrderData, CreateSurveyOrderV2MutationVariables>(
      CREATE_SURVEY_ORDER_V2,
      {
        context: { clientName: 'dashboard' },
        onCompleted: () => {
          setIsOrderPlaced(true)
        },
        onError: (error) => {
          setFailedToLaunch(true)
          setLaunchErrorDetails(getLaunchErrorDetails(error))
          captureApolloError(
            LoggerErrorType.ApolloMutation,
            'createSurveyOrder',
            error
          )
        }
      }
    )

  const toggleDialogOpen: () => void = () => {
    const { adjustedStartDate } = getStartAndEndDatesAdjusted(
      selectedDate,
      estimatedDeliveryDays
    )
    setSelectedDate(new Date(adjustedStartDate))
    setIsPurchaseGoLiveClicked(!isPurchaseGoLiveClicked)
    setHasCostChanged(false)
    setFailedToLaunch(false)
    setLaunchErrorDetails(undefined)
  }

  const handlePlaceOrder = async () => {
    setIsPlacingOrder(true)
    setFailedToLaunch(false)
    setLaunchErrorDetails(undefined)
    try {
      if (
        surveyId &&
        feasibility !== undefined &&
        costPerIncidence &&
        panelSupplierCode !== null
      ) {
        // have a final check on the cost quote
        // show an error message when cost has changed silently
        // i.e IR slackbot or via gql playground
        const { data: newCostDetails } = await refetchGetCostQuote({
          projectId,
          surveyId,
          lengthOfInterview: estimatedLengthOfInterview
        })
        const newCostPerIncidence = newCostDetails.getCostQuote?.cpi.amount
        setHasCostChanged(costPerIncidence !== newCostPerIncidence)

        // this is to match the cost details shown to customer
        // with the final check
        if (costPerIncidence === newCostPerIncidence) {
          const { adjustedStartDate, adjustedEndDate } =
            getStartAndEndDatesAdjusted(selectedDate, maxEstimatedDeliveryDays)
          // make sure fieldwork is always updated before placing survey order
          await updateFieldworkV2({
            variables: {
              input: {
                surveyId,
                panelSupplierCode: panelSupplierCode,
                lengthOfInterview: estimatedLengthOfInterview,
                startDate: adjustedStartDate,
                endDate: adjustedEndDate,
                countryCode: null,
                samplesNeeded: null
              }
            }
          })

          const setSurveyLive = getSurveyLiveConfig()
          await createSurveyOrderV2({
            variables: {
              surveyId,
              feasibility,
              customerCPI: costPerIncidence,
              baseCPI: baseAmount,
              panelSupplierCode: panelSupplierCode,
              ...(setSurveyLive !== undefined && { setSurveyLive })
            }
          })
        }
      }
    } catch (error) {
      setFailedToLaunch(true)
      setLaunchErrorDetails(getLaunchErrorDetails(error))
    } finally {
      setIsPlacingOrder(false)
    }
  }

  const goToLaunchedProject = () => {
    navigate(getResultsPath(surveyId))
  }

  if (isOrderPlaced) {
    return (
      <SurveyLaunchedPage
        onButtonClick={goToLaunchedProject}
        surveyLaunchDate={formattedStartDate}
      />
    )
  }

  const isDateSelectionLoading =
    loadingFeasibilityEstimate ||
    loadingFieldwork ||
    questionnaireLoading ||
    loadingFieldworkEstimate

  const isCostBreakdownLoading =
    loadingFieldwork ||
    loadingCostDetails ||
    loadingFeasibilityEstimate ||
    questionnaireLoading ||
    loadingCountAudienceQuestions

  const isProjectOverviewLoading =
    loadingFieldwork ||
    questionnaireLoading ||
    loadingFieldworkEstimate ||
    loadingAudience

  const shouldIgnoreFeasibility =
    localStorage.getItem('ignoreFeasibility') === 'true'

  const rightButtonDisabled = !(
    !hasValidationErrors() &&
    isAudienceValid &&
    (shouldIgnoreFeasibility || isFeasible) &&
    isQuestionnairePresent &&
    (shouldIgnoreFeasibility || !loadingFeasibilityEstimate) &&
    !hasIncompleteTranslations
  )

  const statusComponent = (
    <Alert
      severity={AlertType.ERROR}
      style={{ marginBottom: theme.spacing(2) }}
    >
      There is a change in cost. Please check the final cost below before <br />
      proceeding.
    </Alert>
  )

  return (
    <>
      <ConfirmationLayout
        startDateSelection={
          isDateSelectionLoading ? (
            <ConfirmationStartDateLoader />
          ) : (
            <ConfirmationStartDate
              estimatedTimeToComplete={maxEstimatedDeliveryDays}
              completionDate={maxEndDate}
              displayedDate={selectedDate}
              panel={panelSupplierName}
            />
          )
        }
        reviewDataQuality={<ConfirmationReviewDataQuality />}
        costBreakdown={
          isCostBreakdownLoading && !loadingFieldwork ? (
            <ConfirmationCostBreakdownLoader />
          ) : (
            <ConfirmationCostBreakdown
              completes={wantedCompletes}
              costPerComplete={costPerIncidence}
              totalCost={totalCost}
              onPlaceOrder={handlePlaceOrder}
              hasError={failedToLaunch}
              errorDetails={launchErrorDetails}
              onShowPreview={() => {
                if (!panelSupplierCode) {
                  throw new Error('Panel supplier code is undefined')
                }
                handleShowPreview(surveyId, panelSupplierCode)
              }}
              isOpen={isPurchaseGoLiveClicked}
              toggleIsOpen={toggleDialogOpen}
              confirmButtonDisabled={rightButtonDisabled}
              completionDate={maxEndDate}
              surveyLaunchDate={formattedStartDate}
              loadingCreateSurveyOrder={
                loadingCreateSurveyOrderV2 || isPlacingOrder
              }
              statusComponent={hasCostChanged ? statusComponent : undefined}
              showCostBreakdown={showCostBreakdown}
            />
          )
        }
        projectOverview={
          isProjectOverviewLoading ? (
            <ConfirmationProjectOverviewLoader />
          ) : (
            <ConfirmationProjectOverview
              locale={locale}
              completes={wantedCompletes}
              lengthOfInterview={estimatedLengthOfInterview}
              numberOfQuestions={countTotalQuestions}
            />
          )
        }
      />
      <BottomNav
        width={'calc(100% - 56px)'}
        rightButtonText="Confirm"
        onClickRightButton={toggleDialogOpen}
        secondaryRightButtonText="Preview"
        onClickSecondaryRightButton={() => {
          if (!panelSupplierCode) {
            throw new Error('Panel supplier code is undefined')
          }
          handleShowPreview(surveyId, panelSupplierCode)
        }}
        secondaryRightButtonDisabled={!isQuestionnairePresent}
        rightButtonDisabled={rightButtonDisabled}
      />
    </>
  )
}

export default Confirmation
